import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styled from "styled-components";

import {
  BackButton,
  Box,
  ButtonStyled,
  LabelStyled,
  NotAuthorizedForResource,
  SpanStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import { AddCabinetWrapper } from "./styled";
import { ContentWrapper } from "../../../components/styled/styled";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import CabinetsApi from "../../../services/api/cabinetsApi";
import RequestsApi from "../../../services/api/requestsApi";
import { ValidatedInputWrapper } from "../../../components/common/inputFields/ValidatedInput";
import { FormGroup } from "react-bootstrap";
import AccessSettings from "./components/AccessSettings";
import CustomRadioButton from "../../../components/common/radioButtons/CustomRadio";
import {
  PlaceholderImg,
  MenuTriggerCross,
} from "../../../components/styled/AllImages";
import { toUTCISOStringFromDateTime } from "../../../utils";

const ASSET_URL = process.env.REACT_APP_ASSET_URL;

const EditCabinet = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessThroughRequest, setAccessThroughRequest] = useState([]);
  const [initialValues, setInitialValues] = useState({
    action: "update",
    cabinet_id: id,
    is_temporary: 0,
    cabinet_name: "",
    description: "",
    setting_id: null,
    my_department_id: null,
    my_dept_ids: [],
    other_department_id: null,
    other_dept_ids: [],
    referrals: [],
    from_date: null,
    from_time: null,
    till_date: null,
    till_time: null,
  });

  const validationSchema = Yup.object().shape(
    {
      cabinet_name: Yup.string().required(t("Cabinet name is required")),
      my_department_id: Yup.number()
        .nullable()
        .when(["other_department_id", "setting_id"], {
          is: (otherId, settingId) => !otherId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),
      other_department_id: Yup.number()
        .nullable()
        .when(["my_department_id", "setting_id"], {
          is: (myId, settingId) => !myId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),
      setting_id: Yup.number()
        .nullable()
        .when(["my_department_id", "other_department_id"], {
          is: (myId, otherId) => !myId && !otherId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),
    },
    [
      ["my_department_id", "other_department_id"],
      ["my_department_id", "setting_id"],
      ["other_department_id", "setting_id"],
    ],
  );

  const getSingleCabinet = async () => {
    try {
      setLoading(true);
      const response = await CabinetsApi.getSingleCabinet(id);

      if (response.code === 200) {

        const cab = response.data.cabinet;

        // Initialize local variables to extract data from cabinet_details array
        let my_department_id = null;
        let my_dept_ids = [];
        let other_department_id = null;
        let other_dept_ids = [];
        let setting_id = null; // Global setting_id if applicable
        let from_branches = [];
        let from_departments = [];
        let checkedUsers = [];

        // Map through cabinet_details to find specific access settings
        cab.cabinet_details?.forEach((detail) => {
          if (detail.group_type === "my_department") {
            my_department_id = detail.setting_id;
            my_dept_ids = detail.selected_users || [];
          } else if (detail.group_type === "other_department") {
            other_department_id = detail.setting_id;
            other_dept_ids = detail.selected_users || detail.selected_departments || [];
            from_branches = detail.from_branches;
            from_departments = detail.from_departments;
            checkedUsers = detail?.selected_users_data?.map((item) => {
              const uKey = `${detail.group_type}_${item.id}`;
              const matchingReferral = response.data.cabinet.referrals?.find(
                (ref) => ref.referred_to_id === item.id && ref.type === detail.group_type
              );

              return {
                ...item,
                subLabel:
                  language === "en"
                    ? `${item?.user_detail?.department_selective?.name_en} (${item?.user_detail?.office_location_selective?.name_en})`
                    : `${item?.user_detail?.department_selective?.name_ar} (${item?.user_detail?.office_location_selective?.name_ar})`,
                
                // Adding the Referral Data
                referralUniqueKey: uKey,
                referralNote: matchingReferral ? matchingReferral.note : "",
                shareReferrals: !!matchingReferral,
              };
            }) || null;
          } else if (detail.group_type === "myself") {
            setting_id = detail.setting_id
          } 
        });

        setInitialValues({
          action: "update",
          cabinet_id: cab.id,
          cabinet_name: cab.name || "",
          description: cab.description || "",
          is_temporary: cab.is_temporary,
          // If the main cabinet setting_id is 10 (Myself), we use that,
          // otherwise it's derived from the details above
          setting_id: setting_id === 10 ? 10 : null,
          my_department_id: my_department_id,
          my_dept_ids: my_dept_ids,
          other_department_id: other_department_id,
          other_dept_ids: other_dept_ids,
          referrals: cab.referrals?.map((ref) => ({
            unique_id: `${ref.type}_${ref.referred_to_id}`,
            user_id: ref.referred_to_id,
            is_share: 1,
            note: ref.note,
            group: ref.type
          })) || [],

          checkedUsers: checkedUsers,
          from_branches: from_branches,
          from_departments: from_departments,
          
          checkedHqBranches: {
            branchIds: from_branches,
            depIds: from_departments,
          },

          // Date/Time handling
          from_date: cab.time_limit_start
            ? new Date(cab.time_limit_start)
            : null,
          from_time: cab.time_limit_start
            ? new Date(cab.time_limit_start)
            : null,
          till_date: cab.time_limit_end ? new Date(cab.time_limit_end) : null,
          till_time: cab.time_limit_end ? new Date(cab.time_limit_end) : null,
        });

        // Handle the request-based access section
        setAccessThroughRequest(
          cab.access_granted_requests.map((item) => ({
            ...item,
            profile: item?.from_user?.profile
              ? `${ASSET_URL}/${item?.from_user?.profile}`
              : null,
          })),
        );
      } else if (response.code === 403) {
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error(t("Error_fetching_data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getSingleCabinet();
  }, [id, language]);

  const handleRemoveRequest = async (requestId) => {
    try {
      const response = await RequestsApi.removeRequest({ id: requestId });
      if (response.code === 200) {
        setAccessThroughRequest((prev) =>
          prev.filter((item) => item.id !== requestId),
        );
        toast.success(t("Access removed"));
      }
    } catch (error) {
      toast.error(t("Error removing access"));
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        ...values,
        time_limit_start:
          parseInt(values.is_temporary) === 1
            ? toUTCISOStringFromDateTime(values.from_date, values.from_time)
            : null,
        time_limit_end:
          parseInt(values.is_temporary) === 1
            ? toUTCISOStringFromDateTime(values.till_date, values.till_time)
            : null,
      };
      const response = await CabinetsApi.createCabinet(payload); 
      if (response.code === 200) {
        toast.success(t("cabinet_updated"));
        navigate("/cabinets");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <ThemeLoader />;
  if (accessDenied) return <NotAuthorizedForResource />;

  return (
    <AddCabinetWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("edit_cabinet")}
            onClick={() => navigate("/cabinets")}
          />
          <Breadcrumbs />
        </Box>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <ContentWrapper>
              <Box>
                <ValidatedInputWrapper>
                  <FormGroup className="formEleContainer">
                    <LabelStyled
                      className="border-0"
                      size="15px"
                      color="var(--text)"
                    >
                      {t("cabinet_name")}
                      <span className="staric">*</span>
                    </LabelStyled>
                    <Box className="w-100">
                      <Field name="cabinet_name" className="form-control" />
                      <ErrorMessage
                        name="cabinet_name"
                        component="div"
                        className="text-danger text-sm mt-1"
                      />
                    </Box>
                  </FormGroup>
                </ValidatedInputWrapper>

                <ValidatedInputWrapper>
                  <FormGroup className="formEleContainer">
                    <LabelStyled
                      className="border-0"
                      size="15px"
                      color="var(--text)"
                    >
                      {t("Description")}
                    </LabelStyled>
                    <Field
                      as="textarea"
                      name="description"
                      className="form-control"
                      style={{ height: "130px" }}
                    />
                  </FormGroup>
                </ValidatedInputWrapper>

                {/* Cabinet Type Section */}
                <Box className="d-flex align-items-center flex-wrap px-4 typeRadioBtnWrapper">
                  <Box className="form-label">
                    <LabelStyled size="15px" color="var(--text)">
                      {t("type")}*
                    </LabelStyled>
                  </Box>
                  <Box className="d-flex align-items-center gap-3 flex-wrap">
                    <CustomRadioButton
                      name="is_temporary"
                      label={t("normal")}
                      value={0}
                      checked={parseInt(values.is_temporary) === 0}
                      disabled={parseInt(values.is_temporary) !== 0}
                    />
                    <CustomRadioButton
                      name="is_temporary"
                      label={t("temporary")}
                      value={1}
                      checked={parseInt(values.is_temporary) === 1}
                      disabled={parseInt(values.is_temporary) !== 1}
                    />
                  </Box>
                </Box>
              </Box>

              <Box>
                <SubHeadingStyled
                  size="18px"
                  weight={700}
                  color={"var(--text)"}
                  className="px-4 pt-4 pb-0"
                >
                  {t("cabinet_access_settings")}
                </SubHeadingStyled>
                <AccessSettings isEdit={true} />
              </Box>

              {/* Access via Request Section */}
              {accessThroughRequest.length > 0 && (
                <Box className="mt-4 px-4">
                  <SubHeadingStyled
                    size="16px"
                    color="var(--text)"
                    className="mb-2"
                  >
                    {t("Access_granted_through_request")}
                  </SubHeadingStyled>
                  <Box className="d-flex gap-2 flex-wrap">
                    {accessThroughRequest.map((item) => (
                      <UserUpload key={item.id}>
                        <Box className="userPlaceholder">
                          <img
                            src={item?.profile || PlaceholderImg}
                            alt="user"
                          />
                        </Box>
                        <Box className="abtUser">
                          <SpanStyled color="var(--text)" size="14px">
                            {item?.from_user?.name}
                          </SpanStyled>
                        </Box>
                        <MenuTriggerCross
                          className="removeTaskTrigger"
                          onClick={() => handleRemoveRequest(item.id)}
                        />
                      </UserUpload>
                    ))}
                  </Box>
                </Box>
              )}

              <Box className="d-flex justify-content-end p-4">
                <Box className="d-flex gap-3">
                  <ButtonStyled
                    label={t("Cancel")}
                    bg={"var(--bgSecondary)"}
                    color={"var(--text2)"}
                    type="button"
                    onClick={() => navigate("/cabinets")}
                  />
                  <ButtonStyled
                    label={t("Update")}
                    type="submit"
                    isLoading={isSubmitting}
                  />
                </Box>
              </Box>
            </ContentWrapper>
          </Form>
        )}
      </Formik>
    </AddCabinetWrapper>
  );
};

export default EditCabinet;

const UserUpload = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 16px;
  background-color: var(--bgSecondary);
  border-radius: 10px;
  width: fit-content;
  .userPlaceholder {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .removeTaskTrigger {
    width: 12px;
    cursor: pointer;
    &:hover {
      fill: var(--redColor);
    }
  }
`;
